// src/pages/EditTaskPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    doc, getDoc, updateDoc, serverTimestamp, collection, query, getDocs, orderBy, where, Timestamp,
    writeBatch, deleteField, addDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/FormPage.module.css';

// Define roles allowed for general task editing (managers)
const managerTaskEditRoles = ['Manager', 'Accounts Manager'];
// Define roles allowed to edit the End Date specifically (typically stricter)
const endDateEditRoles = ['Manager'];
// Define roles that should automatically see all tasks created (for visibleTo array)
const managerRolesForVisibility = ['Manager', 'Accounts Manager'];

const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
        const date = (dateValue instanceof Timestamp) ? dateValue.toDate() : new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        const adjustedDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
        return adjustedDate.toISOString().split('T')[0];
    } catch (error) {
        console.error("Error formatting date for input:", error);
        return '';
    }
};

const EditTaskPage = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userRole, loading: authLoading } = useAuth();

    // Form State
    const [taskName, setTaskName] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [assignedTo, setAssignedTo] = useState([]);
    const [status, setStatus] = useState('');
    const [priority, setPriority] = useState('Medium');
    const [originalTaskName, setOriginalTaskName] = useState('');
    const [selectedClientIds, setSelectedClientIds] = useState([]);

    const [initialTaskData, setInitialTaskData] = useState(null);
    const [initialAssignedTo, setInitialAssignedTo] = useState([]);
    const [initialClientIds, setInitialClientIds] = useState([]);

    const [fieldPermissions, setFieldPermissions] = useState({
        taskName: false, description: false, assignedTo: false, client: false,
        startDate: false, endDate: false, status: false, priority: false,
    });

    const [employees, setEmployees] = useState([]);
    const [clients, setClients] = useState([]);
    const [managerUids, setManagerUids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');

    const isMounted = useRef(true);
    const successTimeoutRef = useRef(null);

    const priorityOptions = ['Low', 'Medium', 'High'];
    const statusOptions = ["Not Started", "In Progress", "Company Approval", "Customer Approval", "Completed", "Cancelled"];

    const intendedEditMode = location.state?.editMode || 'manager';

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        const fetchInitialDropdownData = async () => {
            if (!isMounted.current) return;
            try {
                const usersCol = collection(db, 'users');
                const qAllUsers = query(usersCol, orderBy('name', 'asc'));
                const allUsersSnapshot = await getDocs(qAllUsers);
                if (!isMounted.current) return;
                
                // In the fetchInitialDropdownData useEffect
const employeeList = allUsersSnapshot.docs
.map(docSnapshot => ({
    id: docSnapshot.id,
    name: docSnapshot.data().name || docSnapshot.data().email,
    role: docSnapshot.data().role || 'Unknown' 
}))
.filter(user => user.role !== 'Trainee'); // Explicit trainee exclusion
setEmployees(employeeList);

                const qManagers = query(usersCol, where('role', 'in', managerRolesForVisibility));
                const managerSnapshot = await getDocs(qManagers);
                if (!isMounted.current) return;
                const managerIdList = managerSnapshot.docs.map(docSnapshot => docSnapshot.id);
                setManagerUids(managerIdList);

                const clientsRef = collection(db, 'clients');
                const qClients = query(clientsRef, orderBy('name', 'asc'));
                const clientsSnapshot = await getDocs(qClients);
                if (!isMounted.current) return;
                const clientList = clientsSnapshot.docs.map(docSnapshot => ({ id: docSnapshot.id, name: docSnapshot.data().name }));
                setClients(clientList);

            } catch (err) {
                console.error("Error fetching initial data for dropdowns:", err);
                if (isMounted.current) setError(prev => prev || "Could not load support data for the form.");
                if (isMounted.current) { setEmployees([]); setManagerUids([]); setClients([]); }
            }
        };
        if (currentUser) {
            fetchInitialDropdownData();
        }
    }, [currentUser]);

    useEffect(() => {
        if (authLoading) {
            setLoading(true);
            return;
        }
        if (!currentUser) {
            setError("Authentication required to edit tasks.");
            setLoading(false);
            return;
        }
        if (!taskId) {
            setError("Task ID is missing.");
            setLoading(false);
            return;
        }

        const loadTaskAndDeterminePermissions = async () => {
            setLoading(true);
            setError('');
            try {
                const taskRef = doc(db, 'tasks', taskId);
                const docSnap = await getDoc(taskRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setInitialTaskData(data);

                    setTaskName(data.taskName || '');
                    setOriginalTaskName(data.taskName || '');
                    setDescription(data.description || '');
                    setStartDate(formatDateForInput(data.startDate));
                    setEndDate(formatDateForInput(data.endDate));
                    const currentAssignees = data.assignedTo || [];
                    setAssignedTo([...currentAssignees]);
                    setInitialAssignedTo([...currentAssignees]);
                    setStatus(data.status || 'Not Started');
                    setPriority(data.priority || 'Medium');
                    const currentClientIds = Array.isArray(data.clientIds) ? data.clientIds : (data.clientId ? [data.clientId] : []);
                    setSelectedClientIds([...currentClientIds]);
                    setInitialClientIds([...currentClientIds]);

                    const isManagerUser = managerTaskEditRoles.includes(userRole);
                    const canManagerEditThisEndDate = endDateEditRoles.includes(userRole);
                    const isCreatorUser = data.createdBy === currentUser.uid;

                    let newFieldPermissions = {
                        taskName: false, description: false, assignedTo: false, client: false,
                        startDate: false, endDate: false, status: false, priority: false,
                    };

                    if (isManagerUser) {
                        newFieldPermissions = {
                            taskName: true, description: true, assignedTo: true, client: true,
                            startDate: true, endDate: canManagerEditThisEndDate,
                            status: true, priority: true,
                        };
                    } else if (intendedEditMode === 'creator' && isCreatorUser) {
                        newFieldPermissions = {
                            taskName: true, description: true, assignedTo: true, client: true,
                            startDate: false, endDate: false, status: false, priority: false,
                        };
                    } else {
                        setError("Access Denied: You do not have permission to edit this task or these fields.");
                    }
                    setFieldPermissions(newFieldPermissions);

                } else {
                    setError(`Task with ID "${taskId}" not found.`);
                    setInitialTaskData(null);
                    setTaskName(''); setOriginalTaskName(''); setDescription(''); setStartDate(''); setEndDate('');
                    setAssignedTo([]); setInitialAssignedTo([]); setStatus(''); setPriority('Medium');
                    setSelectedClientIds([]); setInitialClientIds([]);
                    setFieldPermissions({
                        taskName: false, description: false, assignedTo: false, client: false,
                        startDate: false, endDate: false, status: false, priority: false,
                    });
                }
            } catch (err) {
                console.error("Error fetching task for edit:", err);
                setError('Failed to load task data for editing.');
                 setFieldPermissions({taskName: false, description: false, assignedTo: false, client: false, startDate: false, endDate: false, status: false, priority: false });
            } finally {
                if (isMounted.current) setLoading(false);
            }
        };

        loadTaskAndDeterminePermissions();

    }, [taskId, currentUser, userRole, authLoading, intendedEditMode]);


    const filteredClients = useMemo(() => {
        if (!clientSearchTerm) return clients; return clients.filter(client => client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()));
    }, [clients, clientSearchTerm]);

    const filteredEmployees = useMemo(() => {
        if (!assigneeSearchTerm) return employees; return employees.filter(emp => emp.name.toLowerCase().includes(assigneeSearchTerm.toLowerCase()));
    }, [employees, assigneeSearchTerm]);

    const handleInputChange = (setter) => (event) => {
        setter(event.target.value);
    };
    
    const handleAssigneeChange = (event) => {
         const { options } = event.target; const selectedUids = []; for (let i = 0, l = options.length; i < l; i++) { if (options[i].selected) { selectedUids.push(options[i].value); } } setAssignedTo(selectedUids);
     };
    const handleClientChange = (event) => {
        const { options } = event.target; const selectedIds = []; for (let i = 0, l = options.length; i < l; i++) { if (options[i].selected) { selectedIds.push(options[i].value); } } setSelectedClientIds(selectedIds);
    };

    const handleSubmit = async (e) => {
         e.preventDefault(); setError(''); setSuccessMessage('');
         
         if (Object.values(fieldPermissions).every(p => p === false) && !loading && error) {
             setError("You do not have permission to save any changes.");
             return;
         }
         if (Object.values(fieldPermissions).every(p => p === false)) {
            setError("No editable fields permitted for your role or context.");
            return;
         }

         if (fieldPermissions.taskName && !taskName.trim()) { setError("Task name cannot be empty."); return; }
         if (fieldPermissions.client && selectedClientIds.length === 0) { setError("Please select at least one Client."); return; }
         if (fieldPermissions.endDate && !endDate) { setError("End date is required."); return; } 
         if (fieldPermissions.startDate && fieldPermissions.endDate && startDate && endDate && new Date(startDate + "T00:00:00") > new Date(endDate + "T00:00:00")) { setError("End date cannot be before start date."); return; }
         if (fieldPermissions.assignedTo && assignedTo.length === 0) { setError("Task must be assigned to at least one employee."); return; }

         setUpdating(true);
         
         const selectedClientObjects = clients.filter(c => selectedClientIds.includes(c.id));
         const currentSelectedClientNames = selectedClientObjects.map(c => c.name);
         
         const initialAssigneeSet = new Set(initialAssignedTo);
         const newlyAddedAssignees = assignedTo.filter(uid => !initialAssigneeSet.has(uid));
         
         const visibilitySet = new Set(); 
         if (initialTaskData?.createdBy) visibilitySet.add(initialTaskData.createdBy); 
         assignedTo.forEach(uid => visibilitySet.add(uid)); 
         managerUids.forEach(uid => visibilitySet.add(uid)); 
         const visibleToArray = Array.from(visibilitySet);

         const batch = writeBatch(db); 
         let notificationsCreated = 0;
         const activityChanges = [];

         try {
             const taskRef = doc(db, 'tasks', taskId);
             const taskUpdates = {
                 updatedAt: serverTimestamp(),
             };

            if (fieldPermissions.taskName && taskName.trim() !== (initialTaskData?.taskName || '')) {
                taskUpdates.taskName = taskName.trim();
                activityChanges.push({ field: 'Task Name', from: initialTaskData?.taskName || '', to: taskUpdates.taskName });
            }
            if (fieldPermissions.description && description.trim() !== (initialTaskData?.description || '')) {
                taskUpdates.description = description.trim();
                activityChanges.push({ field: 'Description', from: initialTaskData?.description || '', to: taskUpdates.description });
            }
             if (fieldPermissions.assignedTo && JSON.stringify(assignedTo.sort()) !== JSON.stringify(initialAssignedTo.sort())) {
                 taskUpdates.assignedTo = assignedTo;
                 taskUpdates.visibleTo = visibleToArray; 
                 const oldAssigneeNames = initialAssignedTo.map(uid => employees.find(emp => emp.id === uid)?.name || uid).join(', ');
                 const newAssigneeNames = assignedTo.map(uid => employees.find(emp => emp.id === uid)?.name || uid).join(', ');
                 activityChanges.push({ field: 'Assigned To', from: oldAssigneeNames, to: newAssigneeNames });
             } else if (fieldPermissions.assignedTo) { 
                 taskUpdates.visibleTo = visibleToArray;
             }

            if (fieldPermissions.client && JSON.stringify(selectedClientIds.sort()) !== JSON.stringify(initialClientIds.sort())) {
                taskUpdates.clientIds = selectedClientIds;
                taskUpdates.clientNames = currentSelectedClientNames;
                const oldClientNames = initialClientIds.map(id => clients.find(c => c.id === id)?.name || id).join(', ');
                activityChanges.push({ field: 'Clients', from: oldClientNames, to: currentSelectedClientNames.join(', ') });
            }

            if (fieldPermissions.startDate && formatDateForInput(startDate) !== formatDateForInput(initialTaskData?.startDate)) {
                taskUpdates.startDate = startDate ? Timestamp.fromDate(new Date(startDate + 'T00:00:00Z')) : deleteField();
                activityChanges.push({ field: 'Start Date', from: formatDateForInput(initialTaskData?.startDate), to: startDate });
            }
            if (fieldPermissions.endDate && formatDateForInput(endDate) !== formatDateForInput(initialTaskData?.endDate)) {
                taskUpdates.endDate = endDate ? Timestamp.fromDate(new Date(endDate + 'T00:00:00Z')) : deleteField();
                activityChanges.push({ field: 'End Date', from: formatDateForInput(initialTaskData?.endDate), to: endDate });
            }
            if (fieldPermissions.status && status !== initialTaskData?.status) {
                taskUpdates.status = status;
                activityChanges.push({ field: 'Status', from: initialTaskData?.status, to: status });
                if (status === 'Completed' && initialTaskData?.status !== 'Completed') { taskUpdates.completedAt = serverTimestamp(); }
                else if (status !== 'Completed' && initialTaskData?.status === 'Completed' && initialTaskData?.completedAt) { taskUpdates.completedAt = deleteField(); }
                if (status === 'Cancelled' && initialTaskData?.status !== 'Cancelled') { taskUpdates.cancelledAt = serverTimestamp(); }
                else if (status !== 'Cancelled' && initialTaskData?.status === 'Cancelled' && initialTaskData?.cancelledAt) { taskUpdates.cancelledAt = deleteField(); }
            }
            if (fieldPermissions.priority && priority !== initialTaskData?.priority) {
                taskUpdates.priority = priority;
                activityChanges.push({ field: 'Priority', from: initialTaskData?.priority, to: priority });
            }
            
            const hasMeaningfulChanges = Object.keys(taskUpdates).some(key => key !== 'updatedAt' && key !== 'visibleTo') || 
                                        (taskUpdates.visibleTo && JSON.stringify(taskUpdates.visibleTo.sort()) !== JSON.stringify((initialTaskData?.visibleTo || []).sort()));

            if (hasMeaningfulChanges || activityChanges.length > 0) {
                batch.update(taskRef, taskUpdates);

                if (activityChanges.length > 0) {
                    const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
                    let descriptionForLog = `${currentUser.displayName || currentUser.email} updated task.`;
                    if (activityChanges.length === 1) {
                        descriptionForLog = `${currentUser.displayName || currentUser.email} updated ${activityChanges[0].field.toLowerCase()} from "${activityChanges[0].from}" to "${activityChanges[0].to}".`;
                    } else if (activityChanges.length > 1) {
                        const changedFieldNames = activityChanges.map(c => c.field.toLowerCase()).join(', ');
                        descriptionForLog = `${currentUser.displayName || currentUser.email} updated: ${changedFieldNames}.`;
                    }
                    
                    batch.set(activityLogRef, {
                        timestamp: serverTimestamp(),
                        userId: currentUser.uid,
                        userName: currentUser.displayName || currentUser.email,
                        actionType: 'task_edited',
                        description: descriptionForLog,
                        changes: activityChanges 
                    });
                }
            } else {
                 setSuccessMessage("No functional changes detected to save.");
                 setUpdating(false);
                 return;
            }

             if (fieldPermissions.assignedTo && newlyAddedAssignees.length > 0) {
                 const notificationsCol = collection(db, 'notifications'); 
                 const editorName = currentUser.displayName || currentUser.email || 'An Admin'; 
                 const clientNameDisplay = currentSelectedClientNames.length > 0 ? 
                    (currentSelectedClientNames.length > 1 ? `${currentSelectedClientNames.length} clients` : currentSelectedClientNames[0]) 
                    : (initialTaskData?.clientNames && initialTaskData.clientNames.length > 0 ? initialTaskData.clientNames.join(', ') : 'the client');

                 const message = `${editorName} assigned you to the task: "${taskUpdates.taskName || originalTaskName}" for ${clientNameDisplay}`;
                 newlyAddedAssignees.forEach(assigneeUid => { 
                     if (assigneeUid !== currentUser.uid) { 
                        const notificationRef = doc(notificationsCol); 
                        batch.set(notificationRef, { 
                            userId: assigneeUid, taskId: taskId, taskName: taskUpdates.taskName || originalTaskName, 
                            message: message, timestamp: serverTimestamp(), isRead: false, 
                            createdBy: currentUser.uid, createdByName: editorName, type: "task_assignment" 
                        }); 
                        notificationsCreated++; 
                     }
                });
             }

             await batch.commit();

             setSuccessMessage("Task updated successfully!"); 
             if (fieldPermissions.taskName && taskUpdates.taskName) setOriginalTaskName(taskUpdates.taskName);
             if (fieldPermissions.assignedTo) setInitialAssignedTo([...assignedTo]);
             if (fieldPermissions.client) setInitialClientIds([...selectedClientIds]);
             setInitialTaskData(prev => ({...prev, ...taskUpdates, updatedAt: Timestamp.now() })); 
             
             if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
             successTimeoutRef.current = setTimeout(() => {
                if(isMounted.current) {
                   navigate(`/task/${taskId}`); 
                }
            }, 1500); 

         } catch (err) {
             console.error("Error updating task or sending notifications: ", err);
             if (err.code === 'permission-denied') { setError('Permission denied during update.'); } 
             else { setError('Failed to update task. Please try again.'); }
         } finally {
             if (isMounted.current) setUpdating(false);
         }
     };

    if (authLoading || (loading && !initialTaskData) ) { 
        return <div className={styles.loadingIndicator}>Loading task details for editing...</div>; 
    }
    
    if (error && Object.values(fieldPermissions).every(p => p === false) && !loading) { 
        return ( 
            <div className={`${styles.pageContainer} ${styles.errorMessageContainer}`}> 
                <p className={styles.errorMessage}>{error}</p> 
                <button onClick={() => navigate(-1)} className={`${styles.formButton} ${styles.cancelButton}`} style={{marginTop: '1rem'}}>Back to Task Details</button> 
            </div> 
        ); 
    }
    if (!initialTaskData && !loading) {
         return (
            <div className={`${styles.pageContainer} ${styles.errorMessageContainer}`}> 
                <p className={styles.errorMessage}>{error || "Task data could not be loaded or task not found."}</p> 
                <button onClick={() => navigate('/dashboard')} className={`${styles.formButton} ${styles.cancelButton}`} style={{marginTop: '1rem'}}>Back to Dashboard</button> 
            </div> 
        ); 
    }


    return (
        <div className={styles.formContainer}>
            <h2 className={styles.title}>Edit Task: {originalTaskName || 'Loading...'}</h2>

            {error && !successMessage && <p className={styles.errorMessage}>{error}</p>}
            {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.inputGroup}> 
                    <label htmlFor="taskName">Task Name:</label> 
                    <input 
                        type="text" 
                        id="taskName" 
                        value={taskName} 
                        onChange={handleInputChange(setTaskName)} 
                        required 
                        disabled={updating || !fieldPermissions.taskName} 
                    /> 
                </div>

                 <div className={styles.inputGroup}>
                    <label htmlFor="client">Client(s)</label>
                    <input 
                        type="text" 
                        placeholder="Search Clients..." 
                        value={clientSearchTerm} 
                        onChange={handleInputChange(setClientSearchTerm)} 
                        disabled={updating || clients.length === 0 || !fieldPermissions.client} 
                        className={styles.searchInput} 
                    />
                    <select 
                        id="client" 
                        multiple={true} 
                        value={selectedClientIds} 
                        onChange={handleClientChange} 
                        required={fieldPermissions.client} 
                        size="3" 
                        disabled={updating || clients.length === 0 || !fieldPermissions.client} 
                        className={styles.multiSelectInput} 
                    >
                        {filteredClients.length === 0 && clients.length > 0 && <option disabled>No matching clients</option>}
                        {clients.length === 0 && <option disabled>Loading clients...</option>}
                        {filteredClients.map(client => ( <option key={client.id} value={client.id}>{client.name}</option> ))}
                    </select>
                    <small className={styles.selectHelpText}>Hold Ctrl/Cmd to select multiple</small>
                 </div>

                <div className={styles.inputGroup}> 
                    <label htmlFor="description">Description:</label> 
                    <textarea 
                        id="description" 
                        value={description} 
                        onChange={handleInputChange(setDescription)} 
                        disabled={updating || !fieldPermissions.description} 
                        rows={4}
                    /> 
                </div>

                <div className={styles.gridThreeCols}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="startDate">Start Date:</label>
                        <input 
                            type="date" 
                            id="startDate" 
                            value={startDate} 
                            onChange={handleInputChange(setStartDate)} 
                            disabled={updating || !fieldPermissions.startDate} 
                            className={styles.dateInput} 
                        />
                    </div>
                     <div className={styles.inputGroup}>
                         <label htmlFor="endDate">End Date:</label>
                         <input
                             type="date"
                             id="endDate"
                             value={endDate}
                             onChange={handleInputChange(setEndDate)}
                             required={fieldPermissions.endDate} 
                             disabled={updating || !fieldPermissions.endDate} 
                             min={startDate || ''}
                             className={styles.dateInput}
                         />
                     </div>
                     <div className={styles.inputGroup}>
                         <label htmlFor="status">Status:</label>
                         <select 
                            id="status" 
                            value={status} 
                            onChange={handleInputChange(setStatus)} 
                            disabled={updating || !fieldPermissions.status} 
                            required={fieldPermissions.status}
                            className={styles.selectInput}
                        >
                             {statusOptions.map(opt => ( <option key={opt} value={opt}>{opt}</option> ))}
                         </select>
                     </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="priority">Priority</label>
                        <select 
                            id="priority" 
                            value={priority} 
                            onChange={handleInputChange(setPriority)} 
                            disabled={updating || !fieldPermissions.priority} 
                            className={`${styles.prioritySelect} ${styles.selectInput}`} 
                        >
                            {priorityOptions.map(p => ( <option key={p} value={p}>{p}</option> ))}
                        </select>
                    </div>
                </div>

                 <div className={styles.inputGroup}>
                    <label htmlFor="assignedTo">Assign To</label>
                     <input
                         type="text"
                         placeholder="Search Employees..."
                         value={assigneeSearchTerm}
                         onChange={handleInputChange(setAssigneeSearchTerm)}
                         disabled={updating || employees.length === 0 || !fieldPermissions.assignedTo}
                         className={styles.searchInput}
                     />
                    <select 
    id="assignedTo" 
    multiple={true} 
    value={assignedTo} 
    onChange={handleAssigneeChange} 
    disabled={updating || employees.length === 0 || !fieldPermissions.assignedTo} 
>
    {/* Only shows filtered (non-trainee) employees */}
    {filteredEmployees.map(emp => ( 
        <option key={emp.id} value={emp.id}>
            {emp.name}
        </option>
    ))}
</select>
                    <small className={styles.selectHelpText}>Hold Ctrl/Cmd to select multiple</small>
                </div>

                <div className={styles.buttonGroup}>
                    <button type="button" onClick={() => navigate(-1)} className={`${styles.formButton} ${styles.cancelButton}`} disabled={updating}> Cancel </button>
                    <button 
                        type="submit" 
                        className={`${styles.formButton} ${styles.submitButton}`} 
                        disabled={updating || Object.values(fieldPermissions).every(p => p === false)} 
                    >
                        {updating ? <span className={styles.loadingSpinner}></span> : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditTaskPage;